import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import ComboFiltro from '../../../components/ComboFiltro';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import { formatCurrency } from '../../../utils/formatters';
import { API_DOCS } from './FatturePage.api';
import './FatturePage.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoFattura = 'IMMEDIATA' | 'DIFFERITA';
type StatoFattura = 'BOZZA' | 'EMESSA' | 'ANNULLATA';

interface FatturaItem {
  id: number;
  societaId: number;
  sedeId: number | null;
  nominativoId: number | null;
  commessaId: number | null;
  tipoFattura: TipoFattura;
  stato: StatoFattura;
  numero: string | null;
  dataDocumento: string | null;
  dataEmissione: string | null;
  clienteRagioneSociale: string | null;
  totaleImponibile: number | string | null;
  totaleIva: number | string | null;
  totale: number | string | null;
  commessa: { id: number; numero: string; descrizione: string | null } | null;
  tipoDocumento: { id: number; codice: string; nome: string } | null;
  _count: { righe: number };
}

interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_FATTURA: Record<StatoFattura, { label: string; cls: string }> = {
  BOZZA:     { label: 'Bozza',     cls: 'bop-badge-neutral' },
  EMESSA:    { label: 'Emessa',    cls: 'bop-badge-success' },
  ANNULLATA: { label: 'Annullata', cls: 'bop-badge-danger' },
};

const TIPO_FATTURA: Record<TipoFattura, { label: string; cls: string }> = {
  DIFFERITA: { label: 'Differita', cls: 'bop-badge-info' },
  IMMEDIATA: { label: 'Immediata', cls: 'bop-badge-neutral' },
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function renderStato(stato: StatoFattura): React.ReactNode {
  const cfg = STATO_FATTURA[stato] ?? { label: stato, cls: 'bop-badge-neutral' };
  return <span className={`bop-badge ${cfg.cls}`}>{cfg.label}</span>;
}

function renderTipo(tipo: TipoFattura): React.ReactNode {
  const cfg = TIPO_FATTURA[tipo] ?? { label: tipo, cls: 'bop-badge-neutral' };
  return <span className={`bop-badge ${cfg.cls}`}>{cfg.label}</span>;
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function FatturePage() {
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const navigate = useNavigate();

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Filtri
  const [stato, setStato] = useState('');
  const [tipoFattura, setTipoFattura] = useState('');
  const [commessaId, setCommessaId] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati
  const [items, setItems] = useState<FatturaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [commesseOpts, setCommesseOpts] = useState<CommessaOption[]>([]);

  // Debounce ricerca testo (client-side)
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Carica lista
  const caricaLista = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (stato) params.set('stato', stato);
      if (tipoFattura) params.set('tipoFattura', tipoFattura);
      if (commessaId) params.set('commessaId', commessaId);
      const qs = params.toString() ? `?${params.toString()}` : '';

      // @api GET /api/fatture?societaId&stato&tipoFattura&commessaId&nominativoId -> {"success":true,"data":[{"id":"number","societaId":"number","sedeId":"number|null","nominativoId":"number|null","commessaId":"number|null","tipoFattura":"IMMEDIATA|DIFFERITA","stato":"BOZZA|EMESSA|ANNULLATA","numero":"string|null","dataDocumento":"string|null","dataEmissione":"string|null","clienteRagioneSociale":"string|null","totaleImponibile":"number","totaleIva":"number","totale":"number","commessa":{"id":"number","numero":"string","descrizione":"string"},"tipoDocumento":{"id":"number","codice":"string","nome":"string"},"_count":{"righe":"number"}}]}
      const r = await apiFetch<{ success: boolean; data: FatturaItem[] }>(`/api/fatture${qs}`);
      setItems(r.data || []);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento delle fatture'), 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, stato, tipoFattura, commessaId]);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  // Carica commesse per il combo (cambia con societaId effettiva)
  useEffect(() => {
    if (!societaIdEff) {
      setCommesseOpts([]);
      return;
    }
    // @api GET /api/commesse?societaId={id} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?societaId=${societaIdEff}`)
      .then(r => setCommesseOpts(r.commesse || []))
      .catch(() => setCommesseOpts([]));
  }, [societaIdEff]);

  // Resetta commessa se cambia società
  useEffect(() => { setCommessaId(''); }, [societaIdEff]);

  const commesseComboItems = commesseOpts.map(c => ({
    id: String(c.id),
    nome: `${c.numero} — ${c.descrizione || ''}`.trim(),
  }));

  // Ricerca client-side su numero, cliente, commessa
  const itemsFiltrati = useMemo(() => {
    if (!q) return items;
    return items.filter(f => {
      const testo = [
        f.numero || '',
        f.clienteRagioneSociale || '',
        f.commessa?.numero || '',
        f.commessa?.descrizione || '',
      ].join(' ').toLowerCase();
      return testo.includes(q);
    });
  }, [items, q]);

  const pulisciFiltri = () => {
    setStato('');
    setTipoFattura('');
    setCommessaId('');
    setQInput('');
    setQ('');
  };

  return (
    <Layout>
      <div className="fatture-page">
        {/* Header */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Fatture</h1>
          {can('commerciale.fatture_attive', 'edit') && (
            <button
              className="bop-btn bop-btn-primary"
              onClick={() => navigate('/commerciale/attivo/fatture/nuove')}
            >
              + Nuova fatturazione
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar fatture-filters-row">
          {!isDisabled && (
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={societaIdLocale}
                onChange={e => setSocietaIdLocale(e.target.value)}
              >
                {isSuperadmin && <option value="">Tutte</option>}
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Cerca</label>
            <input
              className="bop-input fatture-search-input"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder="Numero, cliente, commessa..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Stato</label>
            <select
              className="bop-select"
              value={stato}
              onChange={e => setStato(e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="BOZZA">Bozza</option>
              <option value="EMESSA">Emessa</option>
              <option value="ANNULLATA">Annullata</option>
            </select>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Tipo</label>
            <select
              className="bop-select"
              value={tipoFattura}
              onChange={e => setTipoFattura(e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="IMMEDIATA">Immediata</option>
              <option value="DIFFERITA">Differita</option>
            </select>
          </div>

          <div className="bop-form-group fatture-combo-group">
            <label className="bop-label">Commessa</label>
            <ComboFiltro
              label="commessa"
              value={commessaId || null}
              onChange={id => setCommessaId(id || '')}
              items={commesseComboItems}
              disabled={!societaIdEff}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">&nbsp;</label>
            <button className="bop-btn bop-btn-secondary" onClick={pulisciFiltri}>
              Pulisci filtri
            </button>
          </div>
        </div>

        {/* Tabella */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per visualizzare le fatture</div>
          </div>
        ) : (
          <div className="bop-card">
            <table className="bop-table bop-table--clickable">
              <thead>
                <tr>
                  <th>Numero</th>
                  <th>Tipo</th>
                  <th>Cliente</th>
                  <th>Commessa</th>
                  <th>Data documento</th>
                  <th className="bop-text-right">Imponibile</th>
                  <th className="bop-text-right">IVA</th>
                  <th className="bop-text-right">Totale</th>
                  <th className="bop-text-right">Righe</th>
                  <th>Stato</th>
                  <th>Tipo doc.</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={11}>
                      <div className="bop-empty-state">Caricamento…</div>
                    </td>
                  </tr>
                ) : itemsFiltrati.length === 0 ? (
                  <tr>
                    <td colSpan={11}>
                      <div className="bop-empty-state">
                        <p className="bop-empty-state__text">
                          Le fatture si compongono dal wizard di fatturazione — differite o
                          immediate — oppure dalle schede SAL, DDT e dagli acconti pagati.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  itemsFiltrati.map(f => (
                    <tr key={f.id} onClick={() => navigate(`/commerciale/attivo/fatture/${f.id}`)}>
                      <td>
                        {f.numero
                          ? <span className="fatture-numero">{f.numero}</span>
                          : <span className="bop-badge bop-badge-neutral">Bozza</span>}
                      </td>
                      <td>{renderTipo(f.tipoFattura)}</td>
                      <td>{f.clienteRagioneSociale || <span className="bop-text-muted">—</span>}</td>
                      <td>
                        {f.commessa ? (
                          <span className="fatture-commessa">
                            {f.commessa.numero}
                            {f.commessa.descrizione && (
                              <span className="bop-text-muted"> — {f.commessa.descrizione}</span>
                            )}
                          </span>
                        ) : (
                          <span className="bop-text-muted">—</span>
                        )}
                      </td>
                      <td>{formatDateIT(f.dataDocumento)}</td>
                      <td className="bop-text-right fatture-importo">
                        {formatCurrency(Number(f.totaleImponibile ?? 0))}
                      </td>
                      <td className="bop-text-right fatture-importo">
                        {formatCurrency(Number(f.totaleIva ?? 0))}
                      </td>
                      <td className="bop-text-right fatture-importo fatture-importo--totale">
                        {formatCurrency(Number(f.totale ?? 0))}
                      </td>
                      <td className="bop-text-right">{f._count?.righe ?? 0}</td>
                      <td>{renderStato(f.stato)}</td>
                      <td className="fatture-tipo-doc">
                        {f.tipoDocumento ? f.tipoDocumento.codice : <span className="bop-text-muted">—</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
